import { createSlice } from "@reduxjs/toolkit";
import {
    getAllSalaryAttachmentsByCaseIdAction,
    getSalaryAttachmentByIdAction,
    createSalaryAttachmentAction,
    updateSalaryAttachmentAction,
    deleteSalaryAttachmentAction,
    clearSalaryAttachmentStateAction,
    setSelectedSalaryAttachmentAction,
} from "../../actions/Case/caseSalaryAttachmentAction";

const initialState = {
    salaryAttachments: [],
    assetOwned: {},
    selectedSalaryAttachment: null,
    loading: false,
    error: null,
    success: false,
    message: "",
};

const salaryAttachmentSlice = createSlice({
    name: "salaryAttachment",
    initialState,
    reducers: {
        resetSalaryAttachmentState: (state) => {
            return initialState;
        },
        clearSalaryAttachmentError: (state) => {
            state.error = null;
            state.message = "";
        },
        updateSalaryAttachmentInList: (state, action) => {
            const index = state.salaryAttachments.findIndex(
                p => p.salaryAttachmentID === action.payload.salaryAttachmentID
            );
            if (index !== -1) {
                state.salaryAttachments[index] = { ...state.salaryAttachments[index], ...action.payload };
            }
        },
        removeSalaryAttachmentFromList: (state, action) => {
            state.salaryAttachments = state.salaryAttachments.filter(
                p => p.salaryAttachmentID !== action.payload
            );
        },
        clearSelectedSalaryAttachment: (state) => {
            state.selectedSalaryAttachment = null;
        }
    },
    extraReducers: (builder) => {
        // =========== GET ALL SALARY ATTACHMENTS BY CASE ID ===========
        builder
            .addCase(getAllSalaryAttachmentsByCaseIdAction.pending, (state) => {
                state.loading = true;
                state.error = null;
                state.success = false;
            })
            .addCase(getAllSalaryAttachmentsByCaseIdAction.fulfilled, (state, action) => {
                state.loading = false;
                state.success = true;
                state.salaryAttachments = action.payload.salaryAttachments;
                state.assetOwned = action.payload.assetOwnedBy;
                state.message = action.payload.message;
            })
            .addCase(getAllSalaryAttachmentsByCaseIdAction.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload?.message || "Failed to fetch salary attachments";
                state.salaryAttachments = [];
            })

        // =========== GET SINGLE SALARY ATTACHMENT BY ID ===========
        builder
            .addCase(getSalaryAttachmentByIdAction.pending, (state) => {
                state.loading = true;
                state.error = null;
                state.success = false;
            })
            .addCase(getSalaryAttachmentByIdAction.fulfilled, (state, action) => {
                state.loading = false;
                state.success = true;
                state.selectedSalaryAttachment = action.payload.salaryAttachment;
                state.message = action.payload.message;
            })
            .addCase(getSalaryAttachmentByIdAction.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload?.message || "Failed to fetch salary attachment";
                state.selectedSalaryAttachment = null;
            })

        // =========== CREATE SALARY ATTACHMENT ===========
        builder
            .addCase(createSalaryAttachmentAction.pending, (state) => {
                state.loading = true;
                state.error = null;
                state.success = false;
            })
            .addCase(createSalaryAttachmentAction.fulfilled, (state, action) => {
                state.loading = false;
                state.success = true;
                state.salaryAttachments.unshift(action.payload.salaryAttachment);
                state.message = action.payload.message;
            })
            .addCase(createSalaryAttachmentAction.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload?.message || "Failed to create salary attachment";
            })

        // =========== UPDATE SALARY ATTACHMENT ===========
        builder
            .addCase(updateSalaryAttachmentAction.pending, (state) => {
                state.loading = true;
                state.error = null;
                state.success = false;
            })
            .addCase(updateSalaryAttachmentAction.fulfilled, (state, action) => {
                state.loading = false;
                state.success = true;

                const index = state.salaryAttachments.findIndex(
                    p => p.salaryAttachmentID === action.payload.salaryAttachmentID
                );
                if (index !== -1) {
                    state.salaryAttachments[index] = action.payload.salaryAttachment;
                }

                if (state.selectedSalaryAttachment?.salaryAttachmentID === action.payload.salaryAttachmentID) {
                    state.selectedSalaryAttachment = action.payload.salaryAttachment;
                }
                state.message = action.payload.message;
            })
            .addCase(updateSalaryAttachmentAction.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload?.message || "Failed to update salary attachment";
            })

        // =========== DELETE SALARY ATTACHMENT ===========
        builder
            .addCase(deleteSalaryAttachmentAction.pending, (state) => {
                state.loading = true;
                state.error = null;
                state.success = false;
            })
            .addCase(deleteSalaryAttachmentAction.fulfilled, (state, action) => {
                state.loading = false;
                state.success = true;

                state.salaryAttachments = state.salaryAttachments.filter(
                    p => p.salaryAttachmentID !== action.payload.salaryAttachmentID
                );

                if (state.selectedSalaryAttachment?.salaryAttachmentID === action.payload.salaryAttachmentID) {
                    state.selectedSalaryAttachment = null;
                }
                state.message = action.payload.message;
            })
            .addCase(deleteSalaryAttachmentAction.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload?.message || "Failed to delete salary attachment";
            })

        // =========== MATCHERS ===========
        builder
            .addMatcher(
                (action) => action.type === clearSalaryAttachmentStateAction.type,
                () => initialState
            )
            .addMatcher(
                (action) => action.type === setSelectedSalaryAttachmentAction.type,
                (state, action) => {
                    state.selectedSalaryAttachment = action.payload;
                }
            );
    }
});

export const {
    resetSalaryAttachmentState,
    clearSalaryAttachmentError,
    updateSalaryAttachmentInList,
    removeSalaryAttachmentFromList,
    clearSelectedSalaryAttachment
} = salaryAttachmentSlice.actions;

export default salaryAttachmentSlice.reducer;
